import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "../../../styles/requirement.css";
import { useLoading } from "../../../context/LoadingContext";
import DataTable from "../components/common/ReusableTable";
import SearchFilter from "../components/common/SearchFilter";
import Header from "../components/layout/Header";
import { getParameterFromManager } from "../../../services/Utils";
import config from "../../../aws-exports";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { v4 as uuidv4 } from "uuid";
import AmtplSidebar from "../components/layout/AMTPLSidebar";
import SidebarCommon from "../components/layout/AMTPLSidebar";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
} from "../services/RequirementServices";

import dayjs from "dayjs";
import AlertModal from "../components/common/AlerModel";

const dataIncoterms = [
  "EXW - Ex-Works (ex works)",
  "FCA – Free Carrier (free carrier)",
  "FOB – Free on Board (free on board)",
  "CIF – Cost, Insurance And Freight (Costs, insurance and freight to the port of destination)",
  "DAP – Delivered At Place (delivered named place)",
  "DAT - Delivered at Terminal (delivered terminal)",
  "CPT – Carriage Paid To (freight paid by)",
  "DDP – Delivered Duty Paid (duty paid free)",
  "CFR – Cost and Freight (cost and freight)",
  "CIP – Carriage And Insurance Paid To (freight and insurance paid by)",
  "FAS – Free Alongside Ship (free alongside ship)",
];

const packageSizeOptions = {
  sack: [
    { value: "25kg-sack", label: "25kg sack" },
    { value: "50kg-sack", label: "50kg sack" },
  ],
  bigBag: [{ value: "1000kg-big-bag", label: "1000kg big bag" }],
};

const TabPanel = ({ children, value, index }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const BasicInfoTab = ({
  category,
  setCategory,
  product,
  setProduct,
  productType,
  setProductType,
  productMaster,
  productCategoryMaster,
  productTypeMaster,
  errors,
  disabled,
}) => {
  return (
    <div className="row g-3">
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.category}>
          <Typography variant="body2">Select Category</Typography>
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            inputProps={{ "aria-label": "Select Category" }}
            disabled={disabled}
          >
            {productCategoryMaster.map((p) => (
              <MenuItem key={p.ProductCategoryID} value={p.ProductCategoryID}>
                {p.CategoryName}
              </MenuItem>
            ))}
          </Select>
          {errors.category && (
            <Typography variant="caption" color="error">
              {errors.category}
            </Typography>
          )}
        </FormControl>
      </div>

      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }} error={!!errors.product}>
          <Typography variant="body2">Select Product</Typography>
          <Select
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            inputProps={{ "aria-label": "Select Product" }}
            disabled={disabled}
          >
            <MenuItem value="" disabled>
              Select Product
            </MenuItem>
            {productMaster.map((p) => (
              <MenuItem key={p.ProductID} value={p.ProductID}>
                {p.ProductName}
              </MenuItem>
            ))}
          </Select>
          {errors.product && (
            <Typography variant="caption" color="error">
              {errors.product}
            </Typography>
          )}
        </FormControl>
      </div>
      <div className="col-md-4">
        <FormControl sx={{ minWidth: "100%" }}>
          <Typography variant="body2">Select Product Type</Typography>
          <Select
            value={productType}
            onChange={(e) => setProductType(e.target.value)}
            displayEmpty
            sx={{ backgroundColor: "white" }}
            inputProps={{ "aria-label": "Select Product Type" }}
            disabled={disabled}
          >
            <MenuItem value="" disabled>
              Select Product Type
            </MenuItem>
            {productTypeMaster.map((p) => (
              <MenuItem key={p.ProductTypeID} value={p.ProductTypeID}>
                {p.ProductTypeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );
};

const GeneralTab = ({
  validUntil,
  setValidUntil,
  incoterm,
  setIncoterm,
  locations,
  setLocations,
  deliveryPeriodFrom,
  setDeliveryPeriodFrom,
  deliveryPeriodTill,
  setDeliveryPeriodTill,
  paymentConditionFiles,
  dataIncoterms,
  errors,
  disabled,
}) => {
  const handleValidUntilChange = useCallback(
    (e) => {
      setValidUntil(e.target.value);
    },
    [setValidUntil]
  );

  const handleIncotermChange = useCallback(
    (e) => {
      setIncoterm(e.target.value);
    },
    [setIncoterm]
  );

  const handleLocationsChange = useCallback(
    (e) => {
      setLocations(e.target.value);
    },
    [setLocations]
  );

  const handleDeliveryPeriodFromChange = useCallback(
    (e) => {
      setDeliveryPeriodFrom(e.target.value);
    },
    [setDeliveryPeriodFrom]
  );

  const handleDeliveryPeriodTillChange = useCallback(
    (e) => {
      setDeliveryPeriodTill(e.target.value);
    },
    [setDeliveryPeriodTill]
  );
  const folder = "public/media/amtpl";
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <div className="mb-3">
          <Typography variant="body2">Valid Until</Typography>
          <TextField
            type="date"
            value={validUntil}
            onChange={handleValidUntilChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.validUntil}
            helperText={errors.validUntil}
            inputProps={{ "aria-label": "Valid Until" }}
            disabled={disabled}
          />
        </div>
        <div className="mb-3">
          <Typography variant="body2">Incoterm</Typography>
          <Select
            value={incoterm}
            onChange={handleIncotermChange}
            displayEmpty
            sx={{ backgroundColor: "white", width: "100%" }}
            inputProps={{ "aria-label": "Select Incoterm" }}
            disabled={disabled}
          >
            <MenuItem value="" disabled>
              Select Incoterm
            </MenuItem>
            {dataIncoterms.map((incotermOption, index) => (
              <MenuItem key={index} value={incotermOption}>
                {incotermOption}
              </MenuItem>
            ))}
          </Select>
        </div>
        <div className="mb-3">
          <Typography variant="body2">Delivery Location</Typography>
          <TextField
            type="text"
            value={locations}
            onChange={handleLocationsChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.locations}
            helperText={errors.locations}
            inputProps={{ "aria-label": "Delivery Location" }}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="col-md-6">
        <div className="mb-3">
          <Typography variant="body2">Delivery Period From</Typography>
          <TextField
            type="date"
            value={deliveryPeriodFrom}
            onChange={handleDeliveryPeriodFromChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.deliveryPeriodFrom}
            helperText={errors.deliveryPeriodFrom}
            disabled={disabled}
            inputProps={{
              "aria-label": "Delivery Period From",
              min: new Date().toISOString().split("T")[0],
            }}
          />
        </div>
        <div className="mb-3">
          <Typography variant="body2">Delivery Period Till</Typography>
          <TextField
            type="date"
            value={deliveryPeriodTill}
            onChange={handleDeliveryPeriodTillChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.deliveryPeriodTill}
            helperText={errors.deliveryPeriodTill}
            inputProps={{
              "aria-label": "Delivery Period Till",
              min: deliveryPeriodFrom || new Date().toISOString().split("T")[0],
            }}
            disabled={disabled}
          />
        </div>
        <div className="mb-3">
          <Typography variant="body2">Payment Condition</Typography>
          <Box sx={{ padding: 1, borderRadius: 1 }}>
            {paymentConditionFiles.length > 0 ? (
              paymentConditionFiles.map((file, index) => (
                <Typography
                  key={file.id || index}
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  <a
                    href={`https://${process.env.REACT_APP_AMTPL_BUCKET}.s3.${config.aws_project_region}.amazonaws.com/public/media/amtpl/${file.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "rgb(150 150 165)",
                      padding: "1px 1px 2px 1px",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ mr: 1 }} />
                  </a>
                </Typography>
              ))
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};

const ProductDetailsTab = ({
  originCountry,
  setOriginCountry,
  termsAndConditions,
  otherFiles,
  countrymaster,
  errors,
  disabled,
}) => {
  const handleOriginCountryChange = useCallback(
    (e) => {
      setOriginCountry(e.target.value);
    },
    [setOriginCountry]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6">
        <div className="mb-3">
          <Typography variant="body2">Origin Country</Typography>
          <Select
            value={originCountry}
            onChange={handleOriginCountryChange}
            displayEmpty
            sx={{ backgroundColor: "white", width: "100%" }}
            error={!!errors.originCountry}
            inputProps={{ "aria-label": "Select Origin Country" }}
            disabled={disabled}
          >
            <MenuItem value="" disabled>
              Select Origin Country
            </MenuItem>
            {countrymaster.map((c) => (
              <MenuItem key={c.ID} value={c.Value}>
                {c.Value}
              </MenuItem>
            ))}
          </Select>
          {errors.originCountry && (
            <Typography variant="caption" color="error">
              {errors.originCountry}
            </Typography>
          )}
        </div>
        <div className="mb-3">
          <Typography variant="body2">Terms & Conditions</Typography>
          <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 1 }}>
            {termsAndConditions.length > 0 ? (
              termsAndConditions.map((file, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  <a
                    href={`https://${process.env.REACT_APP_AMTPL_BUCKET}.s3.${config.aws_project_region}.amazonaws.com/public/media/amtpl/${file.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "rgb(150 150 165)",
                      padding: "1px 1px 2px 1px",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ mr: 1 }} />
                  </a>
                </Typography>
              ))
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </Box>
        </div>
        <div className="mb-3">
          <Typography variant="body2">Other Files</Typography>
          <Box sx={{ backgroundColor: "white", p: 1, borderRadius: 1 }}>
            {otherFiles.length > 0 ? (
              otherFiles.map((file, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                  <a
                    href={`https://${process.env.REACT_APP_AMTPL_BUCKET}.s3.${config.aws_project_region}.amazonaws.com/public/media/amtpl/${file.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "rgb(150 150 165)",
                      padding: "1px 1px 2px 1px",
                    }}
                  >
                    <PictureAsPdfIcon sx={{ mr: 1 }} />
                  </a>
                </Typography>
              ))
            ) : (
              <Typography variant="body2">N/A</Typography>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
};

const QuantityPackagingTab = ({
  productPackaging,
  setProductPackaging,
  packageSize,
  setPackageSize,
  requestedQuantity,
  setRequestedQuantity,
  priceOffered,
  setPriceOffered,
  errors,
  disabled,
}) => {
  const handleProductPackagingChange = useCallback(
    (e) => {
      const value = e.target.value;
      setProductPackaging(value);
      setPackageSize("");
      if (value === "sack") {
        setPackageSize("25kg-sack");
      } else if (value === "bigBag") {
        setPackageSize("1000kg-big-bag");
      }
    },
    [setProductPackaging, setPackageSize]
  );

  const handlePackageSizeChange = useCallback(
    (e) => {
      setPackageSize(e.target.value);
    },
    [setPackageSize]
  );

  const handleRequestedQuantityChange = useCallback(
    (e) => {
      setRequestedQuantity(e.target.value);
    },
    [setRequestedQuantity]
  );

  const handlePriceOfferedChange = useCallback(
    (e) => {
      setPriceOffered(e.target.value);
    },
    [setPriceOffered]
  );

  return (
    <div className="row g-3">
      <div className="col-md-6 mb-3">
        <Typography variant="body2">Product Packaging</Typography>
        <Select
          value={productPackaging}
          onChange={handleProductPackagingChange}
          displayEmpty
          sx={{ backgroundColor: "white", width: "100%" }}
          error={!!errors.productPackaging}
          inputProps={{ "aria-label": "Select Product Packaging" }}
          disabled={disabled}
        >
          <MenuItem value="" disabled>
            Select Product Packaging
          </MenuItem>
          <MenuItem value="sack">Sack</MenuItem>
          <MenuItem value="bigBag">Big Bag</MenuItem>
        </Select>
        {errors.productPackaging && (
          <Typography variant="caption" color="error">
            {errors.productPackaging}
          </Typography>
        )}
      </div>
      {(productPackaging === "sack" || productPackaging === "bigBag") && (
        <div className="col-md-6 mb-3">
          <Typography variant="body2">Package Size</Typography>
          <Select
            value={packageSize}
            onChange={handlePackageSizeChange}
            displayEmpty
            sx={{ backgroundColor: "white", width: "100%" }}
            inputProps={{ "aria-label": "Select Package Size" }}
            disabled={disabled}
          >
            {productPackaging === "sack" &&
              packageSizeOptions.sack.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            {productPackaging === "bigBag" &&
              packageSizeOptions.bigBag.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </div>
      )}
      <div className="row">
        <div className="col-md-6 mb-3">
          <Typography variant="body2">Requested Quantity (in MT)</Typography>
          <TextField
            value={requestedQuantity}
            onChange={handleRequestedQuantityChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            type="number"
            error={!!errors.requestedQuantity}
            helperText={errors.requestedQuantity}
            inputProps={{ "aria-label": "Requested Quantity" }}
            disabled={disabled}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-6 mb-3">
          <Typography variant="body2">Price Offered (in USD/MT)</Typography>
          <TextField
            value={priceOffered}
            onChange={handlePriceOfferedChange}
            sx={{ backgroundColor: "white", width: "100%" }}
            type="number"
            error={!!errors.priceOffered}
            helperText={errors.priceOffered}
            inputProps={{ "aria-label": "Price Offered" }}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
};

const AMTPLRequirementModule = () => {
  const location = useLocation();
  const initialFilter =
    location.state?.selectedFilter || "Requirement Approval";
  const [activeUser, setActiveUser] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const [selectedRow, setSelectedRow] = useState(null);
  const { setLoading } = useLoading();
  const navigate = useNavigate();
  const [INRToUsd, setINRToUSD] = useState(0.012);
  const [tabValue, setTabValue] = useState(0);
  const [category, setCategory] = useState("");
  const [product, setProduct] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [incoterm, setIncoterm] = useState("");
  const [locations, setLocations] = useState("");
  const [deliveryPeriodFrom, setDeliveryPeriodFrom] = useState("");
  const [deliveryPeriodTill, setDeliveryPeriodTill] = useState("");
  const [productPackaging, setProductPackaging] = useState("");
  const [packageSize, setPackageSize] = useState("");
  const [requestedQuantity, setRequestedQuantity] = useState("");
  const [priceOffered, setPriceOffered] = useState("");
  const [originCountry, setOriginCountry] = useState("");
  const [paymentConditionFiles, setPaymentConditionFiles] = useState([]);
  const [termsAndConditions, setTermsAndConditions] = useState([]);
  const [otherFiles, setOtherFiles] = useState([]);
  const [locationImage, setLocationImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRequirementsID, setEditRequirementsID] = useState(null);
  const [isFormActive, setIsFormActive] = useState(false);
  const [productMaster, setProductMaster] = useState([]);
  const [allProductMaster, setAllProductMaster] = useState([]);
  const [countrymaster, setCountryMaster] = useState([]);
  const [requirementList, setRequirementList] = useState([]);
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [productCategoryMaster, setProductCategoryMaster] = useState([]);
  const [productTypeMaster, setProductTypeMaster] = useState([]);
  const [productType, setProductType] = useState("");
  const [disabled] = useState(true); // Add disabled state, set to true
  const [rowclick, setRowClick] = useState(false);
  const [status, setStatus] = useState("");
  const [searchValues, setSearchValues] = useState({
    requirementNumber: "",
    deliveryDate: "",
    requirementDate: "",
    status: "",
  });

  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };
  const loadRequirementProductInfo = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listRequirementProductInfo",
        userOrder: "RequirementProductInfoID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: {
          RequirementsID: requirementsID,
        },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading payment conditions:", error);
      return [];
    }
  }, []);
  const loadProductCategories = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProductCats] = await Promise.all([
          searchfetchQueries({
            target: "listProductCategory",
            userOrder: "CategoryName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);
        console.log(allProductCats);
        setProductCategoryMaster(allProductCats);
      } else {
        setProductCategoryMaster([]);
      }
    } catch (error) {
      console.error("Error loading product categories: ", error);
    } finally {
      setLoading(false);
    }
  }, []);
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductID",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductID",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);

        setAllProductMaster(allProducts);
        setProductMaster(allProducts);
      } else {
        setAllProductMaster([]);
        setProductMaster([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);
  const loadProductTypes = useCallback(
    async (productId) => {
      setLoading(true);
      try {
        const [initialResponse] = await Promise.all([
          fetchData({
            target: "listProductType",
            userOrder: "ProductTypeName",
            rowStart: 0,
            pageSize: 1,
            filterTerms: productId ? { ProductID: productId } : {},
          }),
        ]);

        const totalCount =
          initialResponse?.total || initialResponse?.length || 0;

        if (totalCount > 0) {
          const [allProductTypes] = await Promise.all([
            searchfetchQueries({
              target: "listProductType",
              userOrder: "ProductTypeName", // Fixed typo: was "ProduProductTypeNametID"
              rowStart: 0,
              pageSize: totalCount,
              filterTerms: productId ? { ProductID: productId } : {},
            }),
          ]);

          setProductTypeMaster(allProductTypes || []);
        } else {
          setProductTypeMaster([]);
        }
      } catch (error) {
        console.error("Error loading product types: ", error);
        setProductTypeMaster([]);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to load product types.",
          autoClose: false,
        });
      } finally {
        setLoading(false); // Ensure loader is hidden
      }
    },
    [setLoading]
  );
  const loadCountries = useCallback(async () => {
    setLoading(true);
    try {
      const [allCountries] = await Promise.all([
        fetchData({
          target: "listDropDownOptions",
          userOrder: "DisplayOrder",
          rowStart: 0,
          pageSize: 1,
          filterTerms: {
            Type: "Country",
          },
        }),
      ]);
      if (allCountries.items.length > 0) {
        setCountryMaster(allCountries.items);
      } else {
        setCountryMaster([]);
      }
    } catch (error) {
      console.error("Error loading countries: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadPaymentConditions = useCallback(async (requirementsID) => {
    try {
      const response = await fetchData({
        target: "listPaymentConditions",
        userOrder: "PaymentConditionsID",
        rowStart: 0,
        pageSize: 100,
        filterTerms: {
          RequirementsID: requirementsID,
        },
      });
      return response.items || [];
    } catch (error) {
      console.error("Error loading payment conditions:", error);
      return [];
    }
  }, []);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        } else {
          setActiveUser(user.attributes.email);
        }
        let inrToUsdVal = await getParameterFromManager("INR_TO_USD");
        inrToUsdVal = parseFloat(inrToUsdVal);
        setINRToUSD(inrToUsdVal);
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadProductCategories();
    loadProducts();
    loadCountries();
  }, [navigate, loadProductCategories, loadProducts, loadCountries]);

  useEffect(() => {
    console.log("searchRequirements called....");
    searchRequirements(searchValues);
  }, [rowStart, pageSize, activeUser, searchValues]);

  const handleDataFilterChange = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      setSelectedFilter(selectedValue);
      console.log("Selected filter:", selectedValue);
      if (
        selectedValue === "products" ||
        selectedValue === "offers" ||
        selectedValue === "entities"
      ) {
        navigate("/amtpl", { state: { selectedFilter: selectedValue } });
        console.log(selectedValue);
      } else if (selectedValue === "Add Requirement") {
        navigate("/amtpl/addrequirement", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Requirement Approval") {
        navigate("/amtpl/requirement", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product Offers") {
        navigate("/amtpl/productofferscharges", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Inland Shipping") {
        navigate("/amtpl/inlandshipping", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Country wise Product") {
        navigate("/amtpl/counrywiseproduct", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product Category") {
        navigate("/amtpl/productcategory", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product") {
        navigate("/amtpl/product", {
          state: { selectedFilter: selectedValue },
        });
      }
    },
    [navigate]
  );
  const searchRequirements = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      try {
        const body = {
          target: "listRequirements",
          userOrder: "RequirementsID",
          rowStart: newRowStart,
          pageSize: pageSize,
          filterTerms: {
            ...(searchValue.requirementNumber && {
              RequirementNumber: searchValue.requirementNumber,
            }),
            ...(searchValue.deliveryDate && {
              DeliveryDate: searchValue.deliveryDate,
            }),
            ...(searchValue.requirementDate && {
              RequirementDate: searchValue.requirementDate,
            }),
            ...(searchValue.status && { Status: searchValue.status }),
          },
        };
        const response = await fetchData(body);
        setRequirementList(response.items || response);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading requirements: ", error);
        setRequirementList([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, rowStart, setLoading]
  );

  const parseProductPackaging = (packagingString) => {
    try {
      const cleanedString = packagingString.replace(/[{}]/g, "");
      const keyValuePairs = cleanedString.split(", ").reduce((acc, pair) => {
        const [key, value] = pair.split("=");
        acc[key] = value;
        return acc;
      }, {});
      return {
        item: keyValuePairs.item || "",
        size: keyValuePairs.size || "",
      };
    } catch (error) {
      console.error("Error parsing ProductPackaging:", error);
      return { item: "", size: "" };
    }
  };

  const handleRowClick = useCallback(
    async (row) => {
      setRowClick(true);
      setSelectedRow(row);
      setLoading(true);
      try {
        console.log(selectedRow);

        setEditRequirementsID(row.RequirementsID);
        const requirementProductInfo = await loadRequirementProductInfo(
          row.RequirementsID
        );
        const productInfo = requirementProductInfo[0] || {};

        // Find the ProductCategoryID from the product
        const productData = allProductMaster.find(
          (p) => p.ProductID === row.ProductID
        );
        const productCategoryID = productData?.ProductCategoryID || "";

        // Reset dependent states to prevent stale data
        setProductMaster([]);
        setProductTypeMaster([]);
        setProduct("");
        setProductType("");

        // Set Category and load products
        if (productCategoryID) {
          setCategory(productCategoryID); // Set category
          const filteredProducts = allProductMaster.filter(
            (p) => p.ProductCategoryID === productCategoryID
          );
          setProductMaster(filteredProducts);
        } else {
          setCategory("");
          setProductMaster([]);
        }

        // Set Product and load product types
        if (row.ProductID) {
          setProduct(row.ProductID);
          await loadProductTypes(row.ProductID);
        } else {
          setProduct("");
          setProductTypeMaster([]);
        }

        // Set ProductType
        setProductType(productInfo.ProductTypeID || "");

        // Set other fields
        setValidUntil(
          row.ValidUntil ? dayjs(row.ValidUntil).format("YYYY-MM-DD") : ""
        );
        setIncoterm(row.Incoterm || "");
        setLocations(row.DeliveryLocation || "");
        setDeliveryPeriodFrom(
          row.DeliveryPeriodFrom
            ? dayjs(row.DeliveryPeriodFrom).format("YYYY-MM-DD")
            : ""
        );
        setDeliveryPeriodTill(
          row.DeliveryPeriodTill
            ? dayjs(row.DeliveryPeriodTill).format("YYYY-MM-DD")
            : ""
        );
        setOriginCountry(row.OriginCountry || "");
        setRequestedQuantity(row.RequestedQuantity || row.Quantity || "");
        setPriceOffered(row.PriceOffered || row.Price || "");

        const packaging = parseProductPackaging(row.ProductPackaging || "");
        setProductPackaging(packaging.item);
        setPackageSize(packaging.size);

        const paymentConditions = await loadPaymentConditions(
          row.RequirementsID
        );
        const paymentFiles = paymentConditions
          .filter((pc) => pc.PaymentOptions)
          .map((pc) => ({
            name: pc.PaymentOptions.split("/").pop(),
          }));
        const termsFiles = paymentConditions
          .filter((pc) => pc.TermsConditionsFilePath)
          .map((pc) => ({
            name: pc.TermsConditionsFilePath.split("/").pop(),
          }));
        const otherFilesData = paymentConditions
          .filter((pc) => pc.OthersFilePath)
          .map((pc) => ({
            name: pc.OthersFilePath.split("/").pop(),
          }));

        setPaymentConditionFiles(paymentFiles);
        setTermsAndConditions(termsFiles);
        setOtherFiles(otherFilesData);
        setLocationImage(null);
        setIsFormActive(true);
      } catch (error) {
        console.error("Error loading row data:", error);
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message: "Failed to load requirement data.",
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      setLoading,
      loadPaymentConditions,
      loadRequirementProductInfo,
      allProductMaster,
      loadProductTypes,
    ]
  );

  const confirmApprove = async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "updateRequirementStatus",
        RequirementsID: editRequirementsID,
        Status: "Approved",
        MarketPlacement: "Yes",
      });

      if (response) {
        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: editRequirementsID,
            ActionedByEmail: activeUser,
            Status: "Approved",
            Deleted: 0,
          },
        };
        const res = await postQuery(bodyRTH);
        if (res) {
          setModal((prev) => ({ ...prev, show: false }));
          setModal({
            show: true,
            type: "success",
            title: "Success!",
            message: "Approved successfully.",
            autoClose: false,
          });
          setRowClick(false);
          searchRequirements(searchValues);
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };
  const Approve = () => {
    setModal({
      show: true,
      type: "warning",
      title: "Are you sure?",
      message: "Do you want to Approve?",
      isConfirm: true,
      onConfirm: confirmApprove,
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };
  const Rejected = () => {
    setModal({
      show: true,
      type: "warning",
      title: "Are you sure?",
      message: "Do you want to Reject?",
      isConfirm: true,
      onConfirm: confirmReject,
      onCancel: () => setModal((prev) => ({ ...prev, show: false })),
    });
  };
  const confirmReject = async () => {
    try {
      setLoading(true);
      const response = await fetchData({
        target: "updateRequirementStatus",
        RequirementsID: editRequirementsID,
        Status: "Rejected",
        MarketPlacement: "No",
      });

      if (response) {
        const bodyRTH = {
          target: "createRequirementTxnHistory",
          requirementTxnHistoryInput: {
            RequirementTxnHistoryID: uuidv4(),
            RequirementsID: editRequirementsID,
            ActionedByEmail: activeUser,
            Status: "Rejected",
            Deleted: 0,
          },
        };
        const res = await postQuery(bodyRTH);
        if (res) {
          setModal((prev) => ({ ...prev, show: false }));
          setModal({
            show: true,
            type: "success",
            title: "Success!",
            message: "Rejected successfully.",
            autoClose: false,
          });
          setRowClick(false);
          searchRequirements(searchValues);
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };
  const handlePageChange = (page) => {
    console.log("page:::", page);
    const newRowStart = (page - 1) * pageSize; // Calculate new rowStart
    setRowStart(newRowStart); // Update rowStart
  };
  const fields = [
    {
      name: "requirementNumber",
      label: "Requirement Number",
      type: "text",
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, requirementNumber: value }));
      },
      value: searchValues.requirementNumber,
    },
    {
      name: "deliveryDate",
      label: "Delivery Date",
      type: "date",
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, deliveryDate: value }));
      },
      value: searchValues.deliveryDate,
    },
    {
      name: "requirementDate",
      label: "Requirement Date",
      type: "date",
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, requirementDate: value }));
      },
      value: searchValues.requirementDate,
    },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { value: "In Review", label: "In Review" },
        { value: "Approved", label: "Approved" },
        { value: "Rejected", label: "Rejected" },
      ],
      value: searchValues.status,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, status: value })),
    },
  ];

  const columns = [
    { id: "RequirementNumber", numeric: false, label: "Requirement Number" },
    {
      id: "ProductID",
      numeric: false,
      label: "Product Name",
      renderCell: (row) => {
        const pn = allProductMaster.find(
          (item) => item.ProductID === row.ProductID
        );
        return pn ? pn.ProductName : "";
      },
    },
    { id: "Quantity", numeric: false, label: "Quantity (Per MT)" },
    { id: "Price", numeric: false, label: "Price (USD/ Per MT)" },
    {
      id: "DeliveryDate",
      numeric: false,
      label: "Delivery Date",
      renderCell: (row) =>
        row.DeliveryDate ? dayjs(row.DeliveryDate).format("DD-MM-YYYY") : "",
    },
    {
      id: "RequirementDate",
      numeric: false,
      label: "Requirement Date",
      renderCell: (row) =>
        row.RequirementDate
          ? dayjs(row.RequirementDate).format("DD-MM-YYYY")
          : "",
    },
   
    {
      id: "BuyerType",
      numeric: false,
      label: "Type",
      renderCell: (row) => {
        let btype = "-";
        if (row.CreatedByEmailID === row.GPEmailID) {
          btype = "Global Partner";
        } else if (row.CreatedByEmailID === row.BuyerEmailID) {
          btype = "Buyer";
        }

        return btype;
      },
    },
    {
      id: "CreatedByEmailID",
      numeric: false,
      label: "Created By",
    }, { id: "Status", numeric: false, label: "Status" },
  ];

  const handleSearch = (searchValues) => {
    searchRequirements(searchValues);
    setRowStart(0); // Reset to first page on new search
  };

  const handleTabChange = useCallback((event, newValue) => {
    setTabValue(newValue);
  }, []);

  const handleLocationImageChange = useCallback((event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLocationImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);
  // Calculate current page (1-based index for Pagination)
  const currentPage = Math.floor(rowStart / pageSize) + 1;
  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        isConfirm={modal.isConfirm}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Header activeUser={activeUser} setLoading={setLoading} />
      <div className="content">
        <SidebarCommon
          selectedFilter={selectedFilter}
          handleDataFilterChange={handleDataFilterChange}
        />

        <div className="main-content">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">Requirements</div>
              <div className="head-subtext">
                All Requirements data will be displayed here.
              </div>
            </div>
            <div className="head-btn">
              <div className="export"></div>
            </div>
          </div>
          <div className="search-filter row">
            <SearchFilter fields={fields} onSearch={handleSearch} />
          </div>
          <DataTable
            columns={columns}
            data={requirementList}
            total={totalItems}
            title="Requirement"
            onRowClick={handleRowClick}
            rowsPerPage={pageSize}
            onPageChange={handlePageChange}
            currentPage={currentPage}
          />
          {rowclick ? (
            <div>
              <Box
                sx={{
                  width: "100%",
                  marginTop: 2,
                  boxShadow: "1px 1px 5px #ccc",
                  padding: "15px",
                }}
              >
                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "#ccccccc2",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingBottom: 1,
                    marginBottom: 3,
                  }}
                >
                  <Typography variant="h6">View Requirement Details</Typography>
                  <Box>
                    {selectedRow.Status === "In Review" ? (
                      <>
                        <Button
                          variant="contained"
                          onClick={Approve}
                          className="approvecss"
                        >
                          Approve
                        </Button>
                        <Button
                          variant="contained"
                          className="approvecss"
                          onClick={Rejected}
                        >
                          Reject
                        </Button>
                      </>
                    ) : selectedRow.Status === "Approved" ? null : (
                      <Button
                        variant="contained"
                        onClick={Approve}
                        className="approvecss"
                      >
                        Approve
                      </Button>
                    )}
                    {/* <Button
                  variant="outlined"
                  color="error"
                  onClick={Rejected}
                  sx={{ marginRight: 1 }}
                >
                  Rejected
                </Button> */}
                  </Box>
                </Box>

                <Box
                  sx={{
                    borderBottom: 1,
                    borderColor: "#de6b14",
                  }}
                >
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="Basic info tabs"
                    sx={{
                      "& .MuiTab-root": {
                        color: "#000",
                        backgroundColor: "transparent",
                        "&.Mui-selected": {
                          color: "#fff",
                          backgroundColor: "#de6b14",
                          borderBottom: "2px solid #d4d4d4",
                        },
                      },
                      "& .MuiTabs-indicator": {
                        backgroundColor: "#de6b14",
                      },
                    }}
                  >
                    <Tab
                      label="Basic Info"
                      id="tab-0"
                      aria-controls="tabpanel-0"
                    />
                    <Tab
                      label="General"
                      id="tab-1"
                      aria-controls="tabpanel-1"
                    />
                    <Tab
                      label="Product Details"
                      id="tab-2"
                      aria-controls="tabpanel-2"
                    />
                    <Tab
                      label="Quantity & Packaging"
                      id="tab-3"
                      aria-controls="tabpanel-3"
                    />
                  </Tabs>
                </Box>

                <TabPanel value={tabValue} index={0}>
                  <BasicInfoTab
                    category={category}
                    setCategory={setCategory}
                    product={product}
                    setProduct={setProduct}
                    productType={productType}
                    setProductType={setProductType}
                    productCategoryMaster={productCategoryMaster}
                    productMaster={productMaster}
                    productTypeMaster={productTypeMaster}
                    errors={errors}
                    disabled={disabled}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <GeneralTab
                    validUntil={validUntil}
                    setValidUntil={setValidUntil}
                    incoterm={incoterm}
                    setIncoterm={setIncoterm}
                    locations={locations}
                    setLocations={setLocations}
                    deliveryPeriodFrom={deliveryPeriodFrom}
                    setDeliveryPeriodFrom={setDeliveryPeriodFrom}
                    deliveryPeriodTill={deliveryPeriodTill}
                    setDeliveryPeriodTill={setDeliveryPeriodTill}
                    paymentConditionFiles={paymentConditionFiles}
                    dataIncoterms={dataIncoterms}
                    errors={errors}
                    disabled={disabled}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                  <ProductDetailsTab
                    originCountry={originCountry}
                    setOriginCountry={setOriginCountry}
                    termsAndConditions={termsAndConditions}
                    otherFiles={otherFiles}
                    countrymaster={countrymaster}
                    errors={errors}
                    disabled={disabled}
                  />
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                  <QuantityPackagingTab
                    productPackaging={productPackaging}
                    setProductPackaging={setProductPackaging}
                    packageSize={packageSize}
                    setPackageSize={setPackageSize}
                    requestedQuantity={requestedQuantity}
                    setRequestedQuantity={setRequestedQuantity}
                    priceOffered={priceOffered}
                    setPriceOffered={setPriceOffered}
                    errors={errors}
                    disabled={disabled}
                  />
                </TabPanel>
              </Box>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
    </div>
  );
};

export default AMTPLRequirementModule;
